import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { LayoutGrid, ShoppingCart, CreditCard, ChevronRight, PackageCheck, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const products = [
  { 
    id: 'pdv', 
    name: 'Sistema PDV', 
    icon: <ShoppingCart className="w-6 h-6" />, 
    desc: 'Gestão rápida para vendas presenciais com caixa integrado e off-line.',
    path: '/pdv'
  },
  { 
    id: 'erp', 
    name: 'ERP e Estoque', 
    icon: <LayoutGrid className="w-6 h-6" />, 
    desc: 'Balanço de estoque multicanal em tempo real, compra e emissão de notas.',
    path: '/erp'
  },
  { 
    id: 'conta', 
    name: 'Soluções Financeiras', 
    icon: <CreditCard className="w-6 h-6" />, 
    desc: 'Cobranças, link de pagamento, conciliação e transferência via PIX nativo.',
    path: '/olist'
  },
  { 
    id: 'logistica', 
    name: 'Logística', 
    icon: <PackageCheck className="w-6 h-6" />, 
    desc: 'Logística inteligente para e-commerce com as melhores transportadoras e fretes.',
    path: '/olist'
  }
];

export default function OlistProductSelector() {
  const [active, setActive] = useState('pdv');
  const navigate = useNavigate();

  const activeProduct = products.find(p => p.id === active) || products[0];

  return (
    <section className="py-24 bg-white">
      <div className="container mx-auto px-4 max-w-7xl">
        <div className="flex flex-col items-center mb-16 text-center">
           <span className="text-[#7f00ff] font-bold tracking-[0.2em] uppercase text-sm mb-4">Escolha seu produto</span>
           <p className="text-3xl md:text-5xl font-bold text-slate-900 tracking-tight">O que seu negócio precisa?</p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-12 gap-12 lg:gap-20 items-stretch">
          
          {/* Left Sidebar List */}
          <div className="sm:col-span-5 flex flex-col gap-4">
            {products.map((product) => (
              <button
                key={product.id}
                onClick={() => setActive(product.id)}
                className={`flex items-start gap-6 p-8 rounded-[2rem] transition-all duration-300 text-left border-2 ${
                  active === product.id 
                    ? 'border-[#7f00ff] bg-white shadow-[0_20px_40px_rgba(0,0,0,0.05)] scale-[1.02] z-10' 
                    : 'border-transparent bg-slate-50 opacity-60 hover:opacity-100'
                }`}
              >
                <div className={`p-4 rounded-2xl transition-colors ${
                  active === product.id ? 'bg-[#7f00ff] text-white' : 'bg-white text-slate-400'
                }`}>
                  {product.icon}
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{product.name}</h3>
                  <p className="text-sm text-slate-500 font-medium line-clamp-2">{product.desc}</p>
                </div>
                {active === product.id && <ChevronRight className="w-6 h-6 text-[#7f00ff] mt-2" />}
              </button>
            ))}
          </div>

          {/* Right Content Panel */}
          <div className="sm:col-span-7">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="h-full bg-slate-900 rounded-[3rem] p-8 sm:p-12 lg:p-16 flex flex-col justify-center relative overflow-hidden text-white"
              >
                <div className="flex flex-col sm:flex-row items-center gap-10 lg:gap-16 z-10">
                  <div className="flex-1 order-2 sm:order-1">
                    <div className="flex items-center gap-3 mb-6">
                       <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center">
                          {activeProduct.icon}
                       </div>
                       <span className="text-sm font-bold tracking-widest uppercase opacity-60">Solução Lukos</span>
                    </div>
                    <h4 className="text-3xl lg:text-4xl font-bold mb-6 leading-tight">
                      {activeProduct.name}
                    </h4>
                    <p className="text-lg text-slate-300 font-medium leading-relaxed mb-10">
                      {activeProduct.desc}
                    </p>
                    <button 
                      onClick={() => navigate(activeProduct.path)}
                      className="w-full sm:w-auto bg-[#7f00ff] text-white px-10 py-5 rounded-full font-bold text-lg hover:bg-white hover:text-[#7f00ff] transition-all shadow-xl shadow-purple-600/30"
                    >
                      Saiba mais
                    </button>
                  </div>

                  <div className="flex-1 order-1 lg:order-2 w-full">
                    <div className="relative aspect-square lg:aspect-auto lg:h-[400px] rounded-3xl overflow-hidden border border-white/10 shadow-2xl">
                        <img 
                          src={`https://images.unsplash.com/photo-1551288049-bebda4e38f71?auto=format&fit=crop&q=80&w=1200`} 
                          alt={activeProduct.name}
                          className="w-full h-full object-cover"
                        />
                    </div>
                  </div>
                </div>

                {/* Aesthetic Glow */}
                <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-[#7f00ff]/20 blur-[100px] rounded-full" />
              </motion.div>
            </AnimatePresence>
          </div>

        </div>
      </div>
    </section>
  );
}
